import { useEffect, useState, useMemo } from "react";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import AdminDataModal from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";

interface DropdownOption {
  id: number;
  label: string;
}

interface RegionOption {
  id: number;
  region_name: string;
  status: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  mobile: string;
  group_id: number;
  group_name?: string;
  region_id?: number | null;
  region_name?: string | null;
  status: string;
  image: string;
  created_at: string;
}

const VENDOR_GROUP_ID = 3;

export default function UsersPage() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();

  const [data, setData] = useState<User[]>([]);
  const [groups, setGroups] = useState<DropdownOption[]>([]);
  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  // Only keep the selected group needed for dynamic field display
  const [modalGroupId, setModalGroupId] = useState<number | "">("");

  /* =========================
     FETCH USERS
  ========================= */

  const fetchUsers = async () => {
    const res = await fetch(`${BASE}/admin/users`, {
      credentials: "include",
    });
    const result = await res.json();
    const freshData = result.data || [];
    setData(freshData);

    setSelectedRow((prev: any) =>
      prev ? freshData.find((item: any) => item.id === prev.id) || null : null,
    );

    setActiveRow((prev: any) =>
      prev ? freshData.find((item: any) => item.id === prev.id) || null : null,
    );
  };

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${BASE}/admin/users/groups`, {
        credentials: "include",
      });
      const result = await res.json();

      if (result.success) {
        setGroups(
          result.data.map((g: any) => ({
            id: g.id,
            label: g.name,
          })),
        );
      }
    } catch (error) {
      console.error("Fetch Groups Error:", error);
    }
  };

  const fetchRegions = async () => {
    try {
      const res = await fetch(`${BASE}/admin/regions`, {
        credentials: "include",
      });
      const result = await res.json();
      setRegions(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("Fetch Regions Error:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchGroups();
    fetchRegions();
  }, []);

  const isVendorSelected = Number(modalGroupId) === VENDOR_GROUP_ID;

  /* =========================
     FORM FIELDS
  ========================= */

  const userFields: FormField[] = useMemo(() => {
    const baseFields: FormField[] = [
      { key: "id", label: "ID", colSpan: 1 },
      { key: "name", label: "Name", colSpan: 2 },
      { key: "email", label: "Email", colSpan: 2 },
      { key: "mobile", label: "Mobile", colSpan: 2 },
      { key: "password", label: "Password", colSpan: 2 },
      {
        key: "group_id",
        label: "Group Name",
        type: "select",
        options: groups.map((g) => ({
          value: g.id,
          label: g.label,
        })),
        colSpan: 2,
      },
    ];

    if (isVendorSelected) {
      baseFields.push({
        key: "region_id",
        label: "Region",
        type: "select",
        options: regions
          .filter((r) => r.status === "active")
          .map((r) => ({
            value: r.id,
            label: r.region_name,
          })),
        colSpan: 2,
      });
    }

    baseFields.push(
      {
        key: "image",
        label: "Profile Image",
        type: "image",
        imageType: "single",
        imageFolder: "users",
        uploadField: "profile_image",
        colSpan: 2,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 2,
      },
    );

    return baseFields;
  }, [groups, regions, isVendorSelected]);

  /* =========================
     COLUMNS
  ========================= */

  const columns: Column[] = [
    { key: "id", label: "ID" },
    {
      key: "image",
      label: "Profile",
      imageFolder: "users",
    },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "mobile", label: "Mobile" },
    { key: "group_name", label: "Group Name" },
    { key: "region_name", label: "Region" },
    {
      key: "status",
      label: "Status",
      render: (row: User) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "Created",
      render: (row: User) => new Date(row.created_at).toLocaleDateString(),
    },
  ];

  const buildFormData = (
    formData: any,
    fields: FormField[],
    activeRow?: any,
  ) => {
    const form = new FormData();

    fields.forEach((field) => {
      const value = formData[field.key];

      if (field.type === "image" && field.imageType === "product") {
        if (Array.isArray(value)) {
          value.forEach((img: any, index: number) => {
            if (img?.file) {
              form.append(field.uploadField || "profile_image", img.file);
            }

            if (!img && activeRow?.images?.[index]?.id) {
              form.append("removeImages[]", activeRow.images[index].id);
            }
          });
        }
      } else if (field.type === "image") {
        if (value?.file) {
          form.append(field.uploadField || field.key, value.file);
        } else if (value === "") {
          form.append(field.key, "REMOVE");
        }
      } else {
        form.append(field.key, value ?? "");
      }
    });

    if (Number(formData.group_id) === VENDOR_GROUP_ID) {
      // form.set("region_id", formData.region_id ?? "");
      form.set(
        "region_id",
        formData.region_id ? String(formData.region_id) : "",
      );
    } else {
      form.set("region_id", "");
    }

    return form;
  };

  /* =========================
     HANDLE SUBMIT
  ========================= */

  const handleSubmit = async (formData: any) => {
    try {
      const form = buildFormData(formData, userFields, activeRow);

      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE}/admin/users`
          : `${BASE}/admin/users/${activeRow.id}`;

      const res = await fetch(url, {
        method,
        credentials: "include",
        body: form,
      });

      const result = await res.json();

      if (!res.ok || result.success === false) {
        showModal({
          type: "error",
          title: "Error",
          message:
            result.message ||
            (modalMode === "add"
              ? "Unable to create user."
              : "Unable to update user."),
        });

        return;
      }

      await fetchUsers();

      setModalOpen(false);
      setActiveRow(null);
      setModalGroupId("");

      showModal({
        type: "success",
        title: "Success",
        message:
          result.message ||
          (modalMode === "add"
            ? "User added successfully."
            : "User updated successfully."),
      });
    } catch (error: any) {
      console.error("handleSubmit error:", error);

      showModal({
        type: "error",
        title: "Error",
        message:
          error?.message ||
          (modalMode === "add"
            ? "Unable to create user."
            : "Unable to update user."),
      });
    }
  };

  /* =========================
     DELETE
  ========================= */

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete User",
      message: "Are you sure you want to delete this user?",
      confirmText: "Delete",
      onConfirm: async () => {
        await fetch(`${BASE}/admin/users/${row.id}`, {
          method: "DELETE",
          credentials: "include",
        });
        fetchUsers();
      },
    });
  };

  /* =========================
     HANDLE FORM CHANGE
  ========================= */

  const handleFormChange = (formData: any) => {
    const nextGroupId = formData?.group_id ?? "";
    setModalGroupId((prev) =>
      String(prev) === String(nextGroupId) ? prev : nextGroupId,
    );
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <div>
      <AdminListToolkit
        title="Admin Users"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow({
            id: "",
            name: "",
            email: "",
            mobile: "",
            password: "",
            group_id: "",
            region_id: "",
            image: "",
            status: "active",
          });
          setModalGroupId("");
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalGroupId(row.group_id ?? "");
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalGroupId(row.group_id ?? "");
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport
        exportFileName="users"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Admin User"
        fields={userFields}
        rowData={activeRow}
        onClose={() => {
          setModalOpen(false);
          setActiveRow(null);
          setModalGroupId("");
        }}
        onSubmit={handleSubmit}
        onFormChange={handleFormChange}
        size="lg"
      />
    </div>
  );
}
